import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { resetPassword } from '../api/users';

function ResetPassword() {
    const [email, setEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasUsername, setHasUsername] = useState(false);
    const navigate = useNavigate();
    const toast = useRef(null);

    // Populate email field with username from localStorage on component mount
    useEffect(() => {
        const username = localStorage.getItem('username');
        if (username) {
            setEmail(username);
            setHasUsername(true);
        }
    }, []);

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

    const validateEmail = () => {
        if (!emailRegex.test(email)) {
            toast.current.show({
                severity: 'error',
                summary: 'Invalid Email',
                detail: 'Please enter a valid email address',
                life: 3000
            });
            return false;
        }
        return true;
    };

    const validatePasswords = () => {
        if (!newPassword) {
            toast.current.show({
                severity: 'error',
                summary: 'Invalid Password',
                detail: 'Please enter a new password',
                life: 3000
            });
            return false;
        }
        
        if (newPassword !== confirmPassword) {
            toast.current.show({
                severity: 'error',
                summary: 'Password Mismatch',
                detail: 'Passwords do not match',
                life: 3000
            });
            return false;
        }
        
        if (newPassword.length < 6) {
            toast.current.show({
                severity: 'error',
                summary: 'Weak Password',
                detail: 'Password must be at least 6 characters long',
                life: 3000
            });
            return false;
        }
        
        return true;
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleResetPassword = async () => {
        if (!validateEmail()) return;
        if (!validatePasswords()) return;

        try {
            setIsLoading(true);
            await resetPassword(email, newPassword);
            localStorage.removeItem('username');
            localStorage.removeItem('isLoggedIn');
            localStorage.removeItem('userToken');
            localStorage.removeItem('authToken');
            localStorage.clear();

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Password reset successful! Please log in with your new password.',
                life: 3000
            });
            setTimeout(() => {
                navigate('/logout');
                setTimeout(() => {
                    navigate('/auth');
                }, 100);
            }, 2000);
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to reset password. Please try again.',
                life: 3000
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <Toast ref={toast} position="top-center" />
            <div style={styles.formCard}>
                <h2 style={styles.title}>Reset Your Password</h2>
                <p style={styles.subtitle}>
                    {hasUsername 
                        ? "Update your password below." 
                        : "Enter your email address and new password."
                    }
                </p>

                <div style={styles.formGroup}>
                    <label style={styles.label}>Email</label>
                    <input
                        type="email"
                        placeholder="me@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{
                            ...styles.input,
                            backgroundColor: hasUsername ? '#f5f5f5' : '#ffffff',
                            cursor: hasUsername ? 'not-allowed' : 'text'
                        }}
                        disabled={hasUsername || isLoading}
                    />
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>New Password</label>
                    <div style={styles.passwordContainer}>
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            style={{ ...styles.input, marginBottom: 0 }}
                            disabled={isLoading}
                        />
                        <button 
                            onClick={togglePasswordVisibility} 
                            style={styles.showPasswordButton}
                            type="button"
                        >
                            {showPassword ? "Hide" : "Show"}
                        </button>
                    </div>
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>Confirm New Password</label>
                    <div style={styles.passwordContainer}>
                        <input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm your new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            style={{ ...styles.input, marginBottom: 0 }}
                            disabled={isLoading}
                        />
                        <button 
                            onClick={toggleConfirmPasswordVisibility} 
                            style={styles.showPasswordButton}
                            type="button"
                        >
                            {showConfirmPassword ? "Hide" : "Show"}
                        </button>
                    </div>
                </div>

                <button 
                    onClick={handleResetPassword} 
                    style={styles.button}
                    disabled={isLoading}
                >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backgroundColor: '#f8f9fa',
    },
    formCard: {
        width: '100%',
        maxWidth: '400px',
        padding: '32px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
        margin: '20px auto',
        textAlign: 'center',
    },
    title: {
        fontSize: '28px',
        color: '#264653',
        marginBottom: '16px',
        fontWeight: '600',
    },
    subtitle: {
        fontSize: '15px',
        color: '#666',
        marginBottom: '32px',
        lineHeight: '1.5',
    },
    email: {
        color: '#264653',
        fontWeight: '500',
    },
    formGroup: {
        marginBottom: '24px',
        textAlign: 'left',
    },
    label: {
        display: 'block',
        fontSize: '15px',
        color: '#264653',
        marginBottom: '8px',
        fontWeight: '500',
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        borderRadius: '8px',
        border: '2px solid #e0e0e0',
        transition: 'all 0.2s ease',
    },
    passwordContainer: {
        position: 'relative',
    },
    showPasswordButton: {
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        color: '#2a9d8f',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        padding: '4px 8px',
        borderRadius: '4px',
    },
    codeInputContainer: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        marginBottom: '24px',
    },
    codeInput: {
        width: '40px',
        height: '48px',
        fontSize: '24px',
        textAlign: 'center',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
    },
    button: {
        width: '100%',
        padding: '14px',
        fontSize: '16px',
        backgroundColor: '#264653',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '600',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#1a323d',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        '&:disabled': {
            backgroundColor: '#cccccc',
            cursor: 'not-allowed',
            transform: 'none',
            boxShadow: 'none',
        },
    },
    resendText: {
        fontSize: '14px',
        color: '#666',
        marginTop: '24px',
    },
    resendButton: {
        background: 'none',
        border: 'none',
        color: '#2a9d8f',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        padding: '0',
        textDecoration: 'underline',
        transition: 'color 0.2s ease',
        '&:hover': {
            color: '#1f7268',
        },
        '&:disabled': {
            color: '#cccccc',
            cursor: 'not-allowed',
        },
    },
};

export default ResetPassword;
